import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ErrorType, RootState } from '../../../app/store';
import { AnnouncementTemplateDto, AnnouncementRecipientAlgorithms, AnnouncementTemplateSummaryDto, SentAnnouncementSummaryDto, SentAnnouncementDto } from '@inosio/payments-sdk';

export interface QuickListItem {
  value: AnnouncementRecipientAlgorithms;
  label: string;
}

interface IAnnouncementTemplateDataPage {
  Rows: Array<AnnouncementTemplateSummaryDto>;
}

interface ISentAnnouncementSummaryDataPage{
  Rows: Array<SentAnnouncementSummaryDto>;
}

export type AnnouncementState = {
  //announcement details
  announcementDetail: AnnouncementTemplateDto;
  isGettingAnnouncement: boolean;
  isGettingAnnouncementSuccess: boolean;
  isGettingAnnouncementFailed: boolean;
  AnnouncementDetailError: ErrorType;


  isFetchingAnnouncementTemplates: boolean;
  isFetchingAnnouncementTemplatesFailed: boolean;
  isFetchingAnnouncementTemplatesSuccess: boolean;
  AnnouncementTemplatesResponseDto: any;
  AnnouncementTemplatesDataPages: Array<IAnnouncementTemplateDataPage>;
  AnnouncementTemplatesFetchError: ErrorType;

  announcementEvent: SentAnnouncementDto;
  isGettingAnnouncementEvent: boolean;
  isGettingAnnouncementEventSuccess: boolean;
  isGettingAnnouncementEventFailed: boolean;
  AnnouncementEventError: ErrorType;

  isFetchingSentAnnouncements: boolean;
  isFetchingSentAnnouncementsFailed: boolean;
  isFetchingSentAnnouncementsSuccess: boolean;
  SentAnnouncementResponseDto: any;
  SentAnnouncementsDataPages: Array<ISentAnnouncementSummaryDataPage>;
  SentAnnouncementsFetchError: ErrorType;

  //create new announcement
  isCreatingAnnouncement: boolean;
  isCreatingAnnouncementSuccess: boolean;
  isCreatingAnnouncementFailed: boolean;
  CreateAnnouncementError: ErrorType;

  //clone announcement
  isCloningAnnouncement: boolean;
  isCloningAnnouncementSuccess: boolean;
  isCloningAnnouncementFailed: boolean;
  CloneAnnouncementError: ErrorType;
  cloneAnnouncementDetail: AnnouncementTemplateDto;

  //update announcement
  isUpdatingAnnouncement: boolean;
  isUpdatingAnnouncementSuccess: boolean;
  isUpdatingAnnouncementFailed: boolean;
  UpdateAnnouncementError: ErrorType;

  //delete announcement
  isDeletingAnnouncement: boolean;
  isDeletingAnnouncementSuccess: boolean;
  isDeletingAnnouncementFailed: boolean;
  DeleteAnnouncementError: ErrorType;

  //publishing announcement
  isPublishingAnnouncement: boolean;
  isPublishingAnnouncementSuccess: boolean;
  isPublishingAnnouncementFailed: boolean;
  PublishingAnnouncementDetailError: ErrorType;
};

const getInitialState = (): AnnouncementState => {
  return {
    announcementDetail: {} as AnnouncementTemplateDto,

    isFetchingAnnouncementTemplates: false,
    isFetchingAnnouncementTemplatesFailed: false,
    isFetchingAnnouncementTemplatesSuccess: false,
    AnnouncementTemplatesResponseDto: {},
    AnnouncementTemplatesDataPages: [],
    AnnouncementTemplatesFetchError: {} as ErrorType,

    isFetchingSentAnnouncements: false,
    isFetchingSentAnnouncementsFailed: false,
    isFetchingSentAnnouncementsSuccess: false,
    SentAnnouncementResponseDto: {},
    SentAnnouncementsDataPages: [],
    SentAnnouncementsFetchError: {} as ErrorType,

    isGettingAnnouncement: false,
    isGettingAnnouncementSuccess: false,
    isGettingAnnouncementFailed: false,
    AnnouncementDetailError: {} as ErrorType,

    announcementEvent: {} as SentAnnouncementDto,
    isGettingAnnouncementEvent: false,
    isGettingAnnouncementEventSuccess: false,
    isGettingAnnouncementEventFailed: false,
    AnnouncementEventError: {} as ErrorType,

    isCloningAnnouncement: false,
    isCloningAnnouncementSuccess: false,
    isCloningAnnouncementFailed: false,
    CloneAnnouncementError: {} as ErrorType,
    cloneAnnouncementDetail: {} as AnnouncementTemplateDto,

    isCreatingAnnouncement: false,
    isCreatingAnnouncementSuccess: false,
    isCreatingAnnouncementFailed: false,
    CreateAnnouncementError: {} as ErrorType,

    isUpdatingAnnouncement: false,
    isUpdatingAnnouncementSuccess: false,
    isUpdatingAnnouncementFailed: false,
    UpdateAnnouncementError: {} as ErrorType,

    isDeletingAnnouncement: false,
    isDeletingAnnouncementSuccess: false,
    isDeletingAnnouncementFailed: false,
    DeleteAnnouncementError: {} as ErrorType,

    isPublishingAnnouncement: false,
    isPublishingAnnouncementSuccess: false,
    isPublishingAnnouncementFailed: false,
    PublishingAnnouncementDetailError: {} as ErrorType,
  };
};

const announcementsSlice = createSlice({
  name: 'announcements',
  initialState: getInitialState(),
  reducers: {
    getAnnouncementTemplates(state, action) {
      state.isFetchingAnnouncementTemplates = true;
      state.isFetchingAnnouncementTemplatesFailed = false;
      state.isFetchingAnnouncementTemplatesSuccess = false;
      state.AnnouncementTemplatesFetchError = {} as ErrorType;
    },
    setAnnouncementTemplates(state, action) {
      state.isFetchingAnnouncementTemplates = false;
      state.isFetchingAnnouncementTemplatesSuccess = true;

      let _payload = action.payload as any;

      //get a copy of the current book
      let currentMaintenancePages = [...state.AnnouncementTemplatesDataPages];

      //if startindex = 0, just reset the book because that means it's loading for the first time or he reset the range size
      if (_payload.StartIndex === 0) currentMaintenancePages = [];

      //find out what page we are on
      let whatPageResultsIsThis = Math.ceil((_payload.StartIndex ?? 0) / (_payload?.RangeSize ?? 0));

      //now, add this new data page to our book
      currentMaintenancePages[whatPageResultsIsThis] = {
        Rows: _payload?.Rows,
      };

      //return the full
      state.AnnouncementTemplatesResponseDto = action.payload;
      state.AnnouncementTemplatesDataPages = currentMaintenancePages;
      state.AnnouncementTemplatesFetchError = {} as ErrorType;
    },
    setAnnouncementTemplatesFetchError(state, action) {
      state.isFetchingAnnouncementTemplates = false;
      state.isFetchingAnnouncementTemplatesFailed = true;
      state.isFetchingAnnouncementTemplatesSuccess = false;
      state.AnnouncementTemplatesFetchError = action.payload;
    },
    getSentAnnouncements(state, actions) {
      state.isFetchingSentAnnouncements = true;
      state.isFetchingSentAnnouncementsFailed = false;
      state.isFetchingSentAnnouncementsSuccess = false;
      state.SentAnnouncementsFetchError = {} as ErrorType;
    },
    setSentAnnouncements(state, action) {
      state.isFetchingSentAnnouncements = false;
      state.isFetchingSentAnnouncementsSuccess = true;

      let _payload = action.payload as any;

      //get a copy of the current book
      let currentMaintenancePages = [...state.SentAnnouncementsDataPages];

      //if startindex = 0, just reset the book because that means it's loading for the first time or he reset the range size
      if (_payload.StartIndex === 0) currentMaintenancePages = [];

      //find out what page we are on
      let whatPageResultsIsThis = Math.ceil((_payload.StartIndex ?? 0) / (_payload?.RangeSize ?? 0));

      //now, add this new data page to our book
      currentMaintenancePages[whatPageResultsIsThis] = {
        Rows: _payload?.Rows,
      };

      //return the full
      state.SentAnnouncementResponseDto = action.payload;
      state.SentAnnouncementsDataPages = currentMaintenancePages;
      state.SentAnnouncementsFetchError = {} as ErrorType;
    },
    setSentAnnouncementFetchError(state, action) {
      state.isFetchingSentAnnouncements = false;
      state.isFetchingSentAnnouncementsFailed = true;
      state.isFetchingSentAnnouncementsSuccess = false;
      state.SentAnnouncementsFetchError = action.payload;
    },
    setAnnouncement(state, action) {
      state.announcementDetail = {
        ...state.announcementDetail,
        ...action.payload,
      };
    },
    getAnnouncement(state, action) {
      state.isGettingAnnouncement = true;
      state.isGettingAnnouncementSuccess = false;
      state.isGettingAnnouncementFailed = false;
      state.AnnouncementDetailError = {} as ErrorType;
      state.announcementDetail = {} as AnnouncementTemplateDto;
    },
    getAnnouncementSuccess(state, action: PayloadAction<AnnouncementTemplateDto>) {
      state.isGettingAnnouncement = false;
      state.isGettingAnnouncementSuccess = true;
      state.isGettingAnnouncementFailed = false;
      state.announcementDetail = action.payload;
    },
    getAnnouncementFailed(state, action) {
      state.isGettingAnnouncement = false;
      state.isGettingAnnouncementSuccess = false;
      state.isGettingAnnouncementFailed = true;
      state.AnnouncementDetailError = action.payload;
    },

    getAnnouncementEvent(state, action) {
      state.isGettingAnnouncementEvent = true;
      state.isGettingAnnouncementEventSuccess = false;
      state.isGettingAnnouncementEventFailed = false;
      state.AnnouncementEventError = {} as ErrorType;
      state.announcementEvent = {} as SentAnnouncementDto;
    },
    setAnnouncementEventSuccess(state, action: PayloadAction<SentAnnouncementDto>) {
      state.isGettingAnnouncementEvent = false;
      state.isGettingAnnouncementEventSuccess = true;
      state.isGettingAnnouncementEventFailed = false;
      state.AnnouncementEventError = {} as ErrorType;
      state.announcementEvent = action.payload;
    },
    setAnnouncementEventFailed(state, action) {
      state.isGettingAnnouncementEvent = false;
      state.isGettingAnnouncementEventSuccess = false;
      state.isGettingAnnouncementEventFailed = true;
      state.AnnouncementDetailError = action.payload;
      state.announcementEvent = {} as SentAnnouncementDto
    },
    createAnnouncement(state, action) {
      state.isCreatingAnnouncement = true;
      state.isCreatingAnnouncementFailed = false;
      state.isCreatingAnnouncementSuccess = false;
      state.CreateAnnouncementError = {} as ErrorType;
    },
    setCreateAnnouncement(state, action: PayloadAction<AnnouncementTemplateDto>) {
      state.isCreatingAnnouncement = false;
      state.isCreatingAnnouncementFailed = false;
      state.isCreatingAnnouncementSuccess = true;
      state.announcementDetail = action.payload;
    },
    setCreateAnnouncementFailed(state, action) {
      state.isCreatingAnnouncement = false;
      state.isCreatingAnnouncementFailed = true;
      state.isCreatingAnnouncementSuccess = false;
      state.CreateAnnouncementError = action.payload;
    },
    cloneAnnouncement(state, action) {
      state.isCloningAnnouncement = true;
      state.isCloningAnnouncementFailed = false;
      state.isCloningAnnouncementSuccess = false;
      state.CloneAnnouncementError = {} as ErrorType;
    },
    setCloneAnnouncement(state, action: PayloadAction<AnnouncementTemplateDto>) {
      state.isCloningAnnouncement = false;
      state.isCloningAnnouncementFailed = false;
      state.isCloningAnnouncementSuccess = true;
      state.CloneAnnouncementError = {} as ErrorType;
      state.cloneAnnouncementDetail = action.payload;
    },
    setCloneAnnouncementFailed(state, action) {
      state.isCloningAnnouncement = false;
      state.isCloningAnnouncementFailed = true;
      state.isCloningAnnouncementSuccess = false;
      state.CloneAnnouncementError = action.payload;
    },
    updateAnnouncement(state, action) {
      state.isUpdatingAnnouncement = true;
      state.isUpdatingAnnouncementFailed = false;
      state.isUpdatingAnnouncementSuccess = false;
      state.UpdateAnnouncementError = {} as ErrorType;
    },
    setUpdateAnnouncement(state, action: PayloadAction<AnnouncementTemplateDto>) {
      state.isUpdatingAnnouncement = false;
      state.isUpdatingAnnouncementFailed = false;
      state.isUpdatingAnnouncementSuccess = true;
      state.announcementDetail = action.payload;
    },
    setUpdateAnnouncementFailed(state, action) {
      state.isUpdatingAnnouncement = false;
      state.isUpdatingAnnouncementFailed = true;
      state.isUpdatingAnnouncementSuccess = false;
      state.UpdateAnnouncementError = action.payload;
    },
    deleteAnnouncement(state, action) {
      state.isDeletingAnnouncement = true;
      state.isDeletingAnnouncementFailed = false;
      state.isDeletingAnnouncementSuccess = false;
      state.DeleteAnnouncementError = {} as ErrorType;
    },
    setDeleteAnnouncement(state, action: PayloadAction<AnnouncementTemplateDto>) {
      state.isDeletingAnnouncement = false;
      state.isDeletingAnnouncementFailed = false;
      state.isDeletingAnnouncementSuccess = true;
    },
    setDeleteAnnouncementFailed(state, action) {
      state.isDeletingAnnouncement = false;
      state.isDeletingAnnouncementFailed = true;
      state.isDeletingAnnouncementSuccess = false;
      state.DeleteAnnouncementError = action.payload;
    },
    setPublishAnnouncement(state, action) {
      state.isPublishingAnnouncement = true;
      state.isPublishingAnnouncementSuccess = false;
      state.isPublishingAnnouncementFailed = false;
      state.PublishingAnnouncementDetailError = {} as ErrorType;
    },
    setPublishAnnouncementSuccess(state, action: PayloadAction<AnnouncementTemplateDto>) {
      state.isPublishingAnnouncement = false;
      state.isPublishingAnnouncementSuccess = true;
      state.isPublishingAnnouncementFailed = false;
      state.announcementDetail = action.payload;
    },
    setPublishAnnouncementFailed(state, action) {
      state.isPublishingAnnouncement = false;
      state.isPublishingAnnouncementSuccess = false;
      state.isPublishingAnnouncementFailed = true;
      state.PublishingAnnouncementDetailError = action.payload;
    },
    resetAnnouncementDetailState(state) {
      state.announcementDetail = {} as AnnouncementTemplateDto;
      state.isGettingAnnouncement = false;
      state.isGettingAnnouncementSuccess = false;
      state.isGettingAnnouncementFailed = false;
      state.DeleteAnnouncementError = {} as ErrorType;
    },
    resetDeleteAnnouncementState(state) {
      state.isDeletingAnnouncement = false;
      state.isDeletingAnnouncementFailed = false;
      state.isDeletingAnnouncementSuccess = false;
      state.DeleteAnnouncementError = {} as ErrorType;
    },
    resetCreateAnnouncementState(state) {
      state.isCreatingAnnouncement = false;
      state.isCreatingAnnouncementFailed = false;
      state.isCreatingAnnouncementSuccess = false;
      state.CreateAnnouncementError = {} as ErrorType;
    },
    resetCloneAnnouncementState(state) {
      state.isCloningAnnouncement = false;
      state.isCloningAnnouncementFailed = false;
      state.isCloningAnnouncementSuccess = false;
      state.CloneAnnouncementError = {} as ErrorType;
      state.cloneAnnouncementDetail = {} as AnnouncementTemplateDto;
    },
    resetPublishAnnouncement(state) {
      state.isPublishingAnnouncement = false;
      state.isPublishingAnnouncementSuccess = false;
      state.isPublishingAnnouncementFailed = false;
      state.PublishingAnnouncementDetailError = {} as ErrorType;
    },
    resetAnnouncementEvent(state, action) {
      state.isGettingAnnouncementEvent = false;
      state.isGettingAnnouncementEventSuccess = false;
      state.isGettingAnnouncementEventFailed = false;
      state.AnnouncementEventError = {} as ErrorType;
      state.announcementEvent = {} as SentAnnouncementDto;
    }
  },
});

export const {
  getAnnouncementTemplates,
  setAnnouncementTemplates,
  setAnnouncementTemplatesFetchError,
  getSentAnnouncements,
  setSentAnnouncementFetchError,
  setSentAnnouncements,
  setAnnouncement,
  getAnnouncement,
  getAnnouncementSuccess,
  getAnnouncementFailed,
  getAnnouncementEvent,
  setAnnouncementEventFailed,
  setAnnouncementEventSuccess,
  createAnnouncement,
  setCreateAnnouncement,
  setCreateAnnouncementFailed,
  cloneAnnouncement,
  setCloneAnnouncement,
  setCloneAnnouncementFailed,
  updateAnnouncement,
  setUpdateAnnouncement,
  setUpdateAnnouncementFailed,
  deleteAnnouncement,
  setDeleteAnnouncement,
  setDeleteAnnouncementFailed,
  setPublishAnnouncement,
  setPublishAnnouncementFailed,
  setPublishAnnouncementSuccess,
  resetAnnouncementDetailState,
  resetDeleteAnnouncementState,
  resetCreateAnnouncementState,
  resetCloneAnnouncementState,
  resetPublishAnnouncement,
  resetAnnouncementEvent
} = announcementsSlice.actions;

export default announcementsSlice.reducer;
export const selectAnnouncementsState = (state: RootState) => state.announcementState;
